import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AuthResponse,
  CategoryId,
  CreateCheckinRequest,
  CreateListRequest,
  Friend,
  ImportedEvent,
  List,
  LoginRequest,
  PresignedUploadRequest,
  PresignedUploadResponse,
  SignupRequest,
  User,
  Venue,
} from "@myams/shared";
import { api } from "./api";
import { clearToken, hasTokenSync, setToken } from "./auth";
import { isNative } from "./platform";

// ─── Venues ──────────────────────────────────────────────────────────────────

export function useVenues(params: { cat?: CategoryId | "all" | undefined; q?: string; live?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (params.cat && params.cat !== "all") qs.set("cat", params.cat);
  if (params.q) qs.set("q", params.q);
  if (params.live) qs.set("live", "true");
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: ["venues", params],
    queryFn: () => api<{ venues: Venue[] }>(`/venues${suffix}`, { auth: false }).then((r) => r.venues),
    staleTime: 30_000,
  });
}

export function useVenue(id: string | undefined) {
  return useQuery({
    queryKey: ["venue", id],
    enabled: Boolean(id),
    queryFn: () => api<{ venue: Venue }>(`/venues/${id}`, { auth: false }).then((r) => r.venue),
  });
}

// ─── Imported events (scraper output) ───────────────────────────────────────

export function useImportedEvent(id: string | undefined) {
  return useQuery({
    queryKey: ["imported-event", id],
    enabled: Boolean(id),
    queryFn: () => api<{ event: ImportedEvent }>(`/imports/events/by-id/${encodeURIComponent(id!)}`, { auth: false }).then((r) => r.event),
  });
}

export function useImportedEvents(params: {
  cat?: CategoryId | "all";
  neigh?: string;
  located?: boolean;
  upcoming?: boolean;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.cat && params.cat !== "all") qs.set("cat", params.cat);
  if (params.neigh) qs.set("neigh", params.neigh);
  if (params.located) qs.set("located", "true");
  if (params.upcoming) qs.set("upcoming", "true");
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: ["imported-events", params],
    queryFn: () => api<{ events: ImportedEvent[] }>(`/imports/events${suffix}`, { auth: false }).then((r) => r.events),
    staleTime: 60_000,
  });
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export function useMe(enabled = true) {
  // Skip the /auth/me probe entirely when we know there's no token to send.
  // On native we can't check synchronously, so we always enable.
  const tokenPresent = isNative() || hasTokenSync();
  return useQuery({
    queryKey: ["me"],
    enabled: enabled && tokenPresent,
    retry: false,
    queryFn: () => api<{ user: User }>("/auth/me").then((r) => r.user),
  });
}

/**
 * Compact auth state for routing decisions.
 *   loading — fetching /me with a stored token
 *   authed  — user resolved
 *   anon    — no token, or /me returned 401
 */
export function useAuthStatus(): { status: "loading" | "authed" | "anon"; user: User | null } {
  const tokenPresent = isNative() || hasTokenSync();
  const { data, isPending, isError, fetchStatus } = useMe();
  if (!tokenPresent) return { status: "anon", user: null };
  if (isError) return { status: "anon", user: null };
  if (data) return { status: "authed", user: data };
  if (isPending && fetchStatus !== "idle") return { status: "loading", user: null };
  return { status: "loading", user: null };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginRequest) => api<AuthResponse>("/auth/login", { method: "POST", json: input, auth: false }),
    onSuccess: async (data) => {
      await setToken(data.token);
      qc.setQueryData(["me"], data.user);
    },
  });
}

// ─── Email-code sign-in ─────────────────────────────────────────────────────
//
// PWA-safe replacement for magic links: /request emails a 6-digit code,
// /verify exchanges (email, code) for a JWT. See apps/api/src/routes/magic.ts.

export interface MagicLinkRequestResult {
  ok: boolean;
  delivery: "email" | "dev";
  /** Only present when no email provider was reachable (local dev). */
  devCode?: string;
  reason?: string;
}

export function useMagicLinkRequest() {
  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      api<MagicLinkRequestResult>("/auth/magic/request", { method: "POST", json: { email }, auth: false }),
  });
}

export function useMagicLinkVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      api<AuthResponse>("/auth/magic/verify", { method: "POST", json: { email, code }, auth: false }),
    onSuccess: async (data) => {
      // Set the token BEFORE the component navigates so PublicOnly's
      // useMe → /me resolves authed and doesn't pre-empt the redirect
      // to returnTo.
      await setToken(data.token);
      qc.setQueryData(["me"], data.user);
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignupRequest) => api<AuthResponse>("/auth/signup", { method: "POST", json: input, auth: false }),
    onSuccess: async (data) => {
      await setToken(data.token);
      qc.setQueryData(["me"], data.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await clearToken();
    },
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<User> & { interests?: CategoryId[] }) =>
      api<{ user: User }>("/auth/me", { method: "PATCH", json: input }).then((r) => r.user),
    onSuccess: (user) => qc.setQueryData(["me"], user),
  });
}

// ─── Lists / Saves ───────────────────────────────────────────────────────────

export function useLists() {
  return useQuery({
    queryKey: ["lists"],
    queryFn: () => api<{ lists: List[] }>("/lists").then((r) => r.lists),
  });
}

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateListRequest) => api<{ id: string }>("/lists", { method: "POST", json: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lists"] }),
  });
}

export type SavedEvent = ImportedEvent & { savedAt: number };

interface SavesPayload {
  saves: { venue_id: string; saved_at: number }[];
  venues: { venue_id: string; saved_at: number }[];
  /** Server now joins imported_events so each saved entry is the full
   *  event object plus a savedAt timestamp. Empty array if the user
   *  has no saved events. */
  events: SavedEvent[];
}

/** Venue saves only — existing call sites expect this shape (an array). */
export function useSaves() {
  return useQuery({
    queryKey: ["saves"],
    queryFn: () => api<SavesPayload>("/saves").then((r) => r.venues ?? r.saves ?? []),
  });
}

/** Event saves — array of ImportedEvent (with savedAt). */
export function useEventSaves() {
  return useQuery({
    queryKey: ["event-saves"],
    queryFn: () => api<SavesPayload>("/saves").then((r) => r.events ?? []),
  });
}

export function useToggleSave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ venueId, saved }: { venueId: string; saved: boolean }) =>
      saved ? api(`/saves/${venueId}`, { method: "DELETE" }) : api(`/saves/${venueId}`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saves"] });
      qc.invalidateQueries({ queryKey: ["event-saves"] });
    },
  });
}

export function useToggleEventSave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, saved }: { eventId: string; saved: boolean }) =>
      saved
        ? api(`/saves/events/${encodeURIComponent(eventId)}`, { method: "DELETE" })
        : api(`/saves/events/${encodeURIComponent(eventId)}`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-saves"] });
      qc.invalidateQueries({ queryKey: ["saves"] });
    },
  });
}

// ─── User search ────────────────────────────────────────────────────────────

export interface UserSearchResult {
  id: string;
  name: string;
  handle: string;
  avatarInitial: string;
  avatarTone: string;
  following: boolean;
}

export function useUserSearch(q: string) {
  return useQuery({
    queryKey: ["user-search", q],
    enabled: q.trim().length >= 2,
    queryFn: () => api<{ users: UserSearchResult[] }>(`/users/search?q=${encodeURIComponent(q.trim())}`).then((r) => r.users),
    staleTime: 10_000,
  });
}

export function useToggleFollow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, following }: { userId: string; following: boolean }) =>
      following
        ? api(`/friends/${userId}`, { method: "DELETE" })
        : api(`/friends/${userId}`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["friends"] });
      qc.invalidateQueries({ queryKey: ["user-search"] });
    },
  });
}

// ─── Checkins / Friends ──────────────────────────────────────────────────────

export function useFeed() {
  return useQuery({
    queryKey: ["feed"],
    queryFn: () => api<{ items: any[] }>("/checkins/feed").then((r) => r.items),
  });
}

export function useFriends() {
  return useQuery({
    queryKey: ["friends"],
    queryFn: () => api<{ friends: Friend[] }>("/friends").then((r) => r.friends),
  });
}

export function useCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCheckinRequest) => api<{ id: string }>("/checkins", { method: "POST", json: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feed"] }),
  });
}

// ─── Uploads ─────────────────────────────────────────────────────────────────

export async function presignAndUpload(file: File, scope: PresignedUploadRequest["scope"], targetId?: string): Promise<string> {
  const sign = await api<PresignedUploadResponse>("/uploads/sign", {
    method: "POST",
    json: { scope, targetId, contentType: file.type, extension: file.name.split(".").pop() },
  });
  const res = await fetch(sign.uploadUrl, {
    method: sign.method,
    headers: sign.headers,
    body: file,
  });
  if (!res.ok) throw new Error(`upload_failed_${res.status}`);
  return sign.key;
}

// ─── Admin ───────────────────────────────────────────────────────────────────
//
// All hooks here hit /api/admin/* and require a JWT belonging to a user with
// role === "admin". The route gate (<RequireAdmin>) keeps non-admins off the
// screens that call these, so the hooks themselves don't gate by role.

export interface AdminSource {
  id: string;
  label: string;
  enabled: boolean;
  lastRunAt: number | null;
  lastCompletedAt: number | null;
  totalImported: number;
  totalSeen: number;
  lastError: string | null;
}

export function useAdminSources() {
  return useQuery({
    queryKey: ["admin", "sources"],
    queryFn: () => api<{ sources: AdminSource[] }>("/admin/sources").then((r) => r.sources),
    staleTime: 10_000,
  });
}

export function useToggleSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api(`/admin/sources/${id}`, { method: "PATCH", json: { enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "sources"] }),
  });
}

export function useRunScrape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { source?: string; limit?: number; ignoreLastmod?: boolean; force?: boolean }) =>
      api<{ result?: unknown; results?: unknown }>("/admin/scrape", { method: "POST", json: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "sources"] });
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
      qc.invalidateQueries({ queryKey: ["imported-events"] });
    },
  });
}

export function useAdminCategoryImages() {
  return useMutation({
    mutationFn: () => api<{ result: unknown }>("/admin/ai/category-images", { method: "POST", json: {} }),
  });
}

export function useAdminBackfillEventImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { limit?: number }) =>
      api<{ attempted: number; updated: number }>("/admin/ai/backfill-event-images", { method: "POST", json: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "events"] }),
  });
}

export function useAdminGeocodeBackfill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind?: "venues" | "events" | "all"; limit?: number }) =>
      api<{ result: { venues: number; events: number; attempted: number; errors: number } }>("/admin/geocode/backfill", { method: "POST", json: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
      qc.invalidateQueries({ queryKey: ["venues"] });
      qc.invalidateQueries({ queryKey: ["imported-events"] });
    },
  });
}

// ─── Admin · Users ──────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  handle: string;
  avatarInitial: string;
  avatarTone: string;
  role: "user" | "admin";
  neighborhood: string | null;
  createdAt: number;
}

export function useAdminUsers(q?: string) {
  return useQuery({
    queryKey: ["admin", "users", q ?? ""],
    queryFn: () => {
      const qs = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
      return api<{ users: AdminUser[] }>(`/admin/users${qs}`).then((r) => r.users);
    },
    staleTime: 10_000,
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: "user" | "admin" }) =>
      api(`/admin/users/${id}`, { method: "PATCH", json: { role } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

// ─── Admin · Events ─────────────────────────────────────────────────────────

export function useAdminEvents(q?: string) {
  return useQuery({
    queryKey: ["admin", "events", q ?? ""],
    queryFn: () => {
      // Pull as many as the server will give us (cap 500) — the admin grid
      // sorts/filters client-side, so a single large fetch is faster than
      // re-querying for every sort/filter flip.
      const params = new URLSearchParams({ limit: "500" });
      if (q?.trim()) params.set("q", q.trim());
      return api<{ events: ImportedEvent[] }>(`/admin/events?${params}`).then((r) => r.events);
    },
    staleTime: 10_000,
  });
}

export interface AdminEventInput {
  title?: string;
  description?: string | null;
  imageUrl?: string | null;
  category?: string | null;
  neighborhood?: string | null;
  url?: string;
  startsAt?: number | null;
  endsAt?: number | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  hidden?: boolean;
}

export function useCreateAdminEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminEventInput) =>
      api<{ event: ImportedEvent }>("/admin/events", { method: "POST", json: input }).then((r) => r.event),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
      qc.invalidateQueries({ queryKey: ["imported-events"] });
    },
  });
}

export function useUpdateAdminEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & AdminEventInput) =>
      api<{ event: ImportedEvent }>(`/admin/events/${encodeURIComponent(id)}`, { method: "PATCH", json: input }).then((r) => r.event),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
      qc.invalidateQueries({ queryKey: ["imported-events"] });
      qc.invalidateQueries({ queryKey: ["imported-event", vars.id] });
    },
  });
}

export function useDeleteAdminEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/admin/events/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
      qc.invalidateQueries({ queryKey: ["imported-events"] });
    },
  });
}

// One-shot promote-to-admin. Used by the /admin/bootstrap screen so the
// first admin can be created without curl. After success, ["me"] is
// invalidated so the next render of Profile / AuthGate sees role="admin".
export function useBootstrapAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, userId }: { key: string; userId: string }) =>
      api("/auth/promote", { method: "POST", json: { key, userId }, auth: false }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

// ─── Articles (RSS) ──────────────────────────────────────────────────────────

import type { Article } from "@myams/shared";

export function useArticles(params: { source?: string; category?: string; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.source) qs.set("source", params.source);
  if (params.category) qs.set("category", params.category);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: ["articles", params],
    queryFn: () => api<{ articles: Article[] }>(`/articles${suffix}`, { auth: false }).then((r) => r.articles),
    staleTime: 60_000,
  });
}

export function useArticle(id: string | undefined) {
  return useQuery({
    queryKey: ["article", id],
    enabled: !!id,
    queryFn: () => api<{ article: Article }>(`/articles/${encodeURIComponent(id!)}`, { auth: false }).then((r) => r.article),
  });
}

// ─── Admin · Articles ───────────────────────────────────────────────────────

export function useAdminArticles(q?: string) {
  return useQuery({
    queryKey: ["admin", "articles", q ?? ""],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "500" });
      if (q?.trim()) params.set("q", q.trim());
      return api<{ articles: Article[] }>(`/admin/articles?${params}`).then((r) => r.articles);
    },
    staleTime: 10_000,
  });
}

export interface AdminArticleInput {
  title?: string;
  excerpt?: string | null;
  imageUrl?: string | null;
  author?: string | null;
  category?: string;
  hidden?: boolean;
}

export function useUpdateAdminArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & AdminArticleInput) =>
      api<{ article: Article }>(`/admin/articles/${encodeURIComponent(id)}`, { method: "PATCH", json: input }).then((r) => r.article),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "articles"] });
      qc.invalidateQueries({ queryKey: ["articles"] });
      qc.invalidateQueries({ queryKey: ["article", vars.id] });
    },
  });
}

export function useDeleteAdminArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/admin/articles/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "articles"] });
      qc.invalidateQueries({ queryKey: ["articles"] });
    },
  });
}
